import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { 
  BookOpen, 
  Brain, 
  BarChart3, 
  Shield, 
  Users, 
  Zap, 
  CheckCircle, 
  ArrowRight,
  GraduationCap,
  FileText,
  Bot,
  Globe,
  ChevronRight,
  Star,
  Loader2
} from 'lucide-react';

// Icon mapping for dynamic icons
const iconMap = {
  Brain, BookOpen, BarChart3, Shield, Users, Zap, CheckCircle, 
  ArrowRight, GraduationCap, FileText, Bot, Globe, Star
};

const LandingPage = () => {
  const navigate = useNavigate();
  const [hoveredFeature, setHoveredFeature] = useState(null);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const res = await api.get('/cms/landing-page');
      setContent(res.data);
    } catch (err) {
      console.error('Failed to load CMS content, using defaults');
      // Use default content on error
      setContent(getDefaultContent());
    } finally {
      setLoading(false);
    }
  };

  const getDefaultContent = () => ({
    hero: {
      badge_text: "Powered by AI Grading",
      headline_1: "The Smart Way to",
      headline_2: "Assess & Grade",
      description: "MyExamDesk is the complete examination platform for Cambridge IGCSE schools. Import question papers, create exams, and let AI grade student work using official marking schemes.",
      cta_primary: "Get Started Free",
      cta_secondary: "Learn More"
    },
    stats: {
      stat_1_value: "10k+", stat_1_label: "Questions",
      stat_2_value: "50+", stat_2_label: "Schools",
      stat_3_value: "95%", stat_3_label: "Time Saved"
    },
    features: {
      section_title: "Everything You Need to",
      section_title_highlight: "Excel",
      section_description: "A complete suite of tools designed specifically for Cambridge IGCSE examination and assessment.",
      features: defaultFeatures
    },
    how_it_works: {
      section_title: "How It Works",
      section_description: "Get started in minutes with our simple 3-step process",
      steps: defaultHowItWorks
    },
    pricing: {
      section_title: "Simple, Transparent Pricing",
      section_description: "Choose the plan that fits your school's needs. Upgrade anytime.",
      tiers: defaultPricingTiers
    },
    cta: {
      title: "Ready to Transform Your Assessment Process?",
      description: "Join hundreds of schools already using MyExamDesk to save time and improve student outcomes.",
      button_text: "Start Your Free Trial"
    },
    footer: {
      tagline: "The smart examination platform for modern schools.",
      copyright: "© 2024 MyExamDesk. All rights reserved.",
      domain: "myexamdesk.online"
    }
  });

  // Default content fallbacks
  const defaultFeatures = [
    {
      icon: Brain,
      title: 'AI-Powered Grading',
      description: 'Let GPT-5.2 grade structured questions using Cambridge IGCSE marking schemes. Save hours of manual marking.',
      color: 'from-purple-500 to-indigo-600'
    },
    {
      icon: BookOpen,
      title: '10-Year Question Bank',
      description: 'Access a comprehensive library of past exam papers from IGCSE, organized by subject, topic, and difficulty.',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: BarChart3,
      title: 'Real-Time Analytics',
      description: 'Track student performance with detailed insights, trend analysis, and personalized learning recommendations.',
      color: 'from-emerald-500 to-teal-500'
    },
    {
      icon: FileText,
      title: 'PDF Import Wizard',
      description: 'Upload exam papers and marking schemes. Our AI extracts questions, parts, and mark allocations automatically.',
      color: 'from-orange-500 to-amber-500'
    },
    {
      icon: Users,
      title: 'Multi-School Platform',
      description: 'Manage multiple schools, teachers, and students from a single dashboard with role-based access control.',
      color: 'from-pink-500 to-rose-500'
    },
    {
      icon: Shield,
      title: 'Secure & Reliable',
      description: 'Enterprise-grade security with encrypted data storage and secure exam delivery for all assessments.',
      color: 'from-slate-600 to-slate-800'
    }
  ];

  const defaultHowItWorks = [
    { id: "1", step_number: "1", title: "Import Questions", description: "Upload your exam papers and marking schemes as PDFs. Our AI extracts everything automatically.", color: "indigo" },
    { id: "2", step_number: "2", title: "Create Exams", description: "Build exams from your question bank. Assign to classes and set dates with just a few clicks.", color: "purple" },
    { id: "3", step_number: "3", title: "AI Grades", description: "Let AI grade student submissions using Cambridge marking schemes. Review and approve in seconds.", color: "emerald" }
  ];

  const defaultPricingTiers = [
    { id: "1", name: 'Starter', price: 'Free', description: 'Perfect for small schools getting started', features: ['School-specific Question Bank', 'Basic exam creation', 'Up to 100 students', 'Standard support'], popular: false },
    { id: "2", name: 'Professional', price: '$49/mo', description: 'For schools ready to scale', features: ['Global 10-year Question Bank', 'PDF Import Wizard', 'Unlimited students', 'Advanced analytics', 'Priority support'], popular: true },
    { id: "3", name: 'Premium', price: '$99/mo', description: 'Full power with AI grading', features: ['Everything in Professional', 'AI-Assisted Grading', 'Bulk AI marking', 'Custom marking schemes', 'API access', 'Dedicated support'], popular: false }
  ];

  // Get content with fallbacks
  const hero = content?.hero || getDefaultContent().hero;
  const stats = content?.stats || getDefaultContent().stats;
  const featuresContent = content?.features || getDefaultContent().features;
  const howItWorks = content?.how_it_works || getDefaultContent().how_it_works;
  const pricing = content?.pricing || getDefaultContent().pricing;
  const cta = content?.cta || getDefaultContent().cta;
  const footer = content?.footer || getDefaultContent().footer;

  // Helper to get icon component
  const getIcon = (iconName) => iconMap[iconName] || Star;

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-xl flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                MyExamDesk
              </span>
            </div>
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-slate-600 hover:text-slate-900 transition-colors">Features</a>
              <a href="#pricing" className="text-slate-600 hover:text-slate-900 transition-colors">Pricing</a>
              <a href="#about" className="text-slate-600 hover:text-slate-900 transition-colors">About</a>
            </div>
            <button
              onClick={() => navigate('/login')}
              className="px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-medium hover:from-indigo-700 hover:to-purple-700 transition-all shadow-lg shadow-indigo-500/25 hover:shadow-indigo-500/40"
            >
              Sign In
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 via-white to-purple-50" />
        <div className="absolute top-20 right-0 w-96 h-96 bg-purple-200 rounded-full blur-3xl opacity-30" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-indigo-200 rounded-full blur-3xl opacity-30" />
        
        <div className="max-w-7xl mx-auto relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-100 to-indigo-100 rounded-full text-purple-700 text-sm font-medium mb-6">
                <Bot className="w-4 h-4" />
                {hero.badge_text}
              </div>
              <h1 className="text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
                {hero.headline_1}
                <span className="block bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                  {hero.headline_2}
                </span>
              </h1>
              <p className="text-xl text-slate-600 mb-8 leading-relaxed">
                {hero.description}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => navigate('/contact')}
                  className="px-8 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-semibold text-lg hover:from-indigo-700 hover:to-purple-700 transition-all shadow-xl shadow-indigo-500/30 hover:shadow-indigo-500/50 flex items-center justify-center gap-2"
                >
                  {hero.cta_primary}
                  <ArrowRight className="w-5 h-5" />
                </button>
                <button
                  onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
                  className="px-8 py-4 bg-white text-slate-700 rounded-xl font-semibold text-lg border-2 border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-all flex items-center justify-center gap-2"
                >
                  {hero.cta_secondary}
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
              <div className="flex items-center gap-8 mt-10 pt-10 border-t border-slate-200">
                <div>
                  <p className="text-3xl font-bold text-slate-900">{stats.stat_1_value}</p>
                  <p className="text-sm text-slate-500">{stats.stat_1_label}</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-slate-900">{stats.stat_2_value}</p>
                  <p className="text-sm text-slate-500">{stats.stat_2_label}</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-slate-900">{stats.stat_3_value}</p>
                  <p className="text-sm text-slate-500">{stats.stat_3_label}</p>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-3xl blur-2xl opacity-20" />
              <div className="relative bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
                <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-4">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-400" />
                    <div className="w-3 h-3 rounded-full bg-yellow-400" />
                    <div className="w-3 h-3 rounded-full bg-green-400" />
                    <span className="ml-4 text-white/80 text-sm">AI Grading Dashboard</span>
                  </div>
                </div>
                <div className="p-6 space-y-4">
                  <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-xl border border-emerald-200">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-6 h-6 text-emerald-600" />
                      <div>
                        <p className="font-medium text-slate-900">Physics Q1(b)</p>
                        <p className="text-sm text-slate-500">P = VI formula correct</p>
                      </div>
                    </div>
                    <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium">1/1</span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-amber-50 rounded-xl border border-amber-200">
                    <div className="flex items-center gap-3">
                      <Zap className="w-6 h-6 text-amber-600" />
                      <div>
                        <p className="font-medium text-slate-900">Physics Q2(a)</p>
                        <p className="text-sm text-slate-500">Partial credit awarded</p>
                      </div>
                    </div>
                    <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium">2/3</span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-purple-50 rounded-xl border border-purple-200">
                    <div className="flex items-center gap-3">
                      <Bot className="w-6 h-6 text-purple-600" />
                      <div>
                        <p className="font-medium text-slate-900">AI Feedback</p>
                        <p className="text-sm text-slate-500">Detailed explanation provided</p>
                      </div>
                    </div>
                    <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">Auto</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-6 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              {featuresContent.section_title}
              <span className="text-indigo-600"> {featuresContent.section_title_highlight}</span>
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              {featuresContent.section_description}
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {(featuresContent.features || defaultFeatures).map((feature, index) => {
              const IconComponent = getIcon(feature.icon);
              return (
                <div
                  key={feature.id || index}
                  className="group relative bg-white rounded-2xl p-8 border border-slate-200 hover:border-transparent hover:shadow-2xl transition-all duration-300 cursor-pointer overflow-hidden"
                  onMouseEnter={() => setHoveredFeature(index)}
                  onMouseLeave={() => setHoveredFeature(null)}
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${feature.color || 'from-blue-500 to-cyan-500'} opacity-0 group-hover:opacity-5 transition-opacity duration-300`} />
                  <div className={`w-14 h-14 bg-gradient-to-br ${feature.color || 'from-blue-500 to-cyan-500'} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <IconComponent className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{feature.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              {howItWorks.section_title}
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              {howItWorks.section_description}
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {(howItWorks.steps || defaultHowItWorks).map((step, index) => (
              <div key={step.id || index} className="text-center">
                <div className={`w-16 h-16 bg-${step.color || 'indigo'}-100 rounded-2xl flex items-center justify-center mx-auto mb-6`}>
                  <span className={`text-2xl font-bold text-${step.color || 'indigo'}-600`}>{step.step_number}</span>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{step.title}</h3>
                <p className="text-slate-600">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-6 bg-gradient-to-br from-slate-900 to-indigo-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              {pricing.section_title}
            </h2>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto">
              {pricing.section_description}
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {(pricing.tiers || defaultPricingTiers).map((tier, index) => (
              <div
                key={tier.id || index}
                className={`relative rounded-2xl p-8 ${tier.popular ? 'bg-gradient-to-br from-purple-50 to-indigo-50 ring-2 ring-indigo-500 scale-105' : 'bg-white'}`}
              >
                {tier.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-medium rounded-full">
                    Most Popular
                  </div>
                )}
                <h3 className="text-2xl font-bold text-slate-900 mb-2">{tier.name}</h3>
                <p className="text-slate-600 mb-4">{tier.description}</p>
                <p className="text-4xl font-bold text-slate-900 mb-6">{tier.price}</p>
                <ul className="space-y-3 mb-8">
                  {(tier.features || []).map((feature, i) => (
                    <li key={i} className="flex items-center gap-3 text-slate-700">
                      <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => navigate('/login')}
                  className={`w-full py-3 rounded-xl font-semibold text-white transition-all ${tier.popular ? 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700' : 'bg-slate-900 hover:bg-slate-800'}`}
                >
                  {tier.button_text || 'Get Started'}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-slate-900 mb-6">
            {cta.title}
          </h2>
          <p className="text-xl text-slate-600 mb-8">
            {cta.description}
          </p>
          <button
            onClick={() => navigate('/contact')}
            className="px-10 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-semibold text-lg hover:from-indigo-700 hover:to-purple-700 transition-all shadow-xl shadow-indigo-500/30 hover:shadow-indigo-500/50"
          >
            {cta.button_text}
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer id="about" className="py-12 px-6 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl flex items-center justify-center">
                  <GraduationCap className="w-6 h-6 text-white" />
                </div>
                <span className="text-xl font-bold">MyExamDesk</span>
              </div>
              <p className="text-slate-400 text-sm">
                {footer.tagline}
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#" className="hover:text-white transition-colors">AI Grading</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Question Bank</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li><a href="#about" className="hover:text-white transition-colors">About Us</a></li>
                <li><a href="/contact" className="hover:text-white transition-colors">Contact</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Careers</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Blog</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li><a href="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="/terms-of-service" className="hover:text-white transition-colors">Terms of Service</a></li>
                <li><a href="/cookie-policy" className="hover:text-white transition-colors">Cookie Policy</a></li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-slate-800 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-slate-400 text-sm">
              {footer.copyright}
            </p>
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <Globe className="w-4 h-4" />
              <span>{footer.domain}</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
